import BaseService from 'services/BaseService';
import {
  Dimension,
  InsightsStatus,
  JobName,
  TaggedCreative,
  TagField,
  VocabStatus,
  VocabValue,
} from 'pages/CreativePerformance/tags/types';

// Reads follow the other services (null on failure; the page shows an error).
// Writes THROW with the API's own message instead: a rejected correction or a
// duplicate name has to tell the person why, not silently do nothing.
function apiMessage(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.message || fallback;
}

class CreativeInsightsService extends BaseService {
  basePath = 'v1/creative-insights';

  async getStatus() {
    await this._getAccessToken();
    return this.handleHttpRequest<InsightsStatus>(
      () => this.http.get(`${this.basePath}/status`),
      'Failed to load tagging status',
    );
  }

  // Queue state only (no warehouse query): what the Tags tab polls while a job runs.
  async getJobs() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ jobs: InsightsStatus['jobs'] }>(
      () => this.http.get(`${this.basePath}/jobs`),
      'Failed to load tagging jobs',
    );
  }

  async getVocabulary() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ values: VocabValue[] }>(
      () => this.http.get(`${this.basePath}/vocabulary`),
      'Failed to load tag lists',
    );
  }

  async getCreatives() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ rows: TaggedCreative[] }>(
      () => this.http.get(`${this.basePath}/creatives`),
      'Failed to load tagged creatives',
    );
  }

  private async write<T>(
    fn: () => Promise<{ data: T }>,
    fallback: string,
  ): Promise<T> {
    await this._getAccessToken();
    try {
      return (await fn()).data;
    } catch (err) {
      throw new Error(apiMessage(err, fallback));
    }
  }

  addValue(value: {
    dimension: Dimension;
    product: string | null;
    label: string;
    definition: string;
  }) {
    return this.write(
      () => this.http.post(`${this.basePath}/vocabulary`, value),
      'Could not add the value',
    );
  }

  updateValue(
    valueId: string,
    patch: { label?: string; definition?: string; status?: VocabStatus },
  ) {
    return this.write(
      () =>
        this.http.patch(
          `${this.basePath}/vocabulary/${encodeURIComponent(valueId)}`,
          patch,
        ),
      'Could not update the value',
    );
  }

  setTag(
    creativeKey: string,
    field: TagField,
    value: string | string[] | null,
  ) {
    return this.write(
      () =>
        this.http.put(
          `${this.basePath}/creatives/${encodeURIComponent(creativeKey)}/tags/${field}`,
          { value },
        ),
      'Could not save the tag',
    );
  }

  startJob(name: JobName, limit?: number) {
    return this.write(
      () => this.http.post(`${this.basePath}/jobs/${name}`, { limit }),
      'Could not start the job',
    );
  }
}

export default CreativeInsightsService;
